import L from "leaflet";
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardMedia from "@mui/material/CardMedia";
import CardContent from "@mui/material/CardContent";
import Avatar from "@mui/material/Avatar";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import CardActionArea from "@mui/material/CardActionArea";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Carousel from "react-material-ui-carousel";
import VideoPlayer from "../../components/VideoPlayer";
import {
  resolveLastUpdated,
  describeAge,
  useAgeTicker,
} from "../../util/CameraAgeUtil";

const API_HOST = process.env.REACT_APP_API_HOST || "/lmiga";

const cameraIcon = L.icon({
  iconUrl: process.env.PUBLIC_URL + "/mapicons/Camera/Camera_A_v9.png",
  iconSize: [21, 21], // size of the icon
  popupAnchor: [0, 0], // point from which the popup should open relative to the iconAnchor
});

// Distinct icon for cameras that have live video (cameraMap.json `video` flag).
const videoCameraIcon = L.icon({
  iconUrl:
    process.env.PUBLIC_URL +
    "/mapicons/JaneByrneCamera/Special_Camera_A_v23.png",
  iconSize: [27, 27],
  iconAnchor: [15, 36],
  popupAnchor: [0, 0],
});

const getLeafletIcon = (feature, latlng, map, pane) => {
  const title = " Camera -  " + getDesc(feature.properties);
  return L.marker(latlng, {
    icon: feature.properties.video ? videoCameraIcon : cameraIcon,
    title: title,
    alt: title,
    pane: pane,
  });
};

const getDesc = (properties) => {
  return properties.locDesc;
};

const PopupTemplate = (props) => {
  return <CameraReport feature={props.feature} />;
};

const CameraReport = (props) => {
  const { feature } = props;
  const encodedId = encodeURIComponent(feature.properties.id);

  const [cameraData, setCameraData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchCamera = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_HOST}/showCamera.json?id=${encodedId}`,
      );
      if (!response.ok) {
        setError(`Error loading camera: ${response.statusText}`);
        setLoading(false);
        return;
      }
      const data = await response.json();
      setCameraData(data);
      setLoading(false);
    } catch (err) {
      setError(`Failed to load camera: ${err.message}`);
      setLoading(false);
    }
  }, [encodedId]);

  useEffect(() => {
    fetchCamera();
  }, [fetchCamera]);

  useAgeTicker(1000);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        {error}
      </Typography>
    );
  }

  const ageText = describeAge(
    resolveLastUpdated(cameraData.directions[0]),
  ).text;

  // Prefer the IDOT district the camera is physically located in (cameraMap.json
  // `idotDistrict`); fall back to the camera's source name when it's unavailable.
  // Districts are 1-9; the server emits 0 (or omits the field) when unknown.
  const idotDistrict = feature.properties.idotDistrict;
  const sourceText =
    idotDistrict > 0 ? `IDOT D${idotDistrict}` : cameraData.sourceName;

  // Poster (still image) shown while the live video loads � the first direction's snapshot.
  const firstDir = cameraData.directions[0];
  const posterUrl = firstDir
    ? firstDir.remoteUrl
      ? firstDir.remoteUrl
      : firstDir.code && firstDir.code !== "NONE"
        ? `${API_HOST}/snapshot?id=${encodedId}&direction=${firstDir.code}`
        : `${API_HOST}/snapshot?id=${encodedId}`
    : undefined;

  return (
    <Card sx={{ borderRadius: 3, minWidth: cameraData.videoUrl ? 380 : 280 }}>
      <CardHeader
        avatar={
          <Avatar
            alt="Camera"
            src={process.env.PUBLIC_URL + "/mapicons/Camera/Camera_A_v9.png"}
            sx={{ width: 21, height: 21 }}
            aria-label="camera"
          />
        }
        title={<font className="layer-title"> Camera </font>}
        className="styledtablerow"
        sx={{ pb: 0, paddingTop: 1, paddingLeft: 1 }}
      />
      <CardContent
        sx={{
          paddingTop: 0,
          paddingBottom: 1,
          paddingLeft: 1,
          paddingRight: 1,
          marginBottom: -2,
        }}
      >
        <Table size="small">
          <TableRow>
            <TableCell sx={{ paddingLeft: 0 }} className="layertablecellLeft">
              <div>
                {cameraData.locationDescription} ({sourceText})
              </div>
              {`Last Updated: ${ageText}`}
            </TableCell>
          </TableRow>
        </Table>

        <Carousel
          autoPlay={!cameraData.videoUrl}
          sx={{ minHeight: 200 }}
          navButtonsProps={{
            size: "small",
            style: {
              borderRadius: 10,
              marginLeft: -10,
              marginRight: -10,
              marginTop: 0,
            },
          }}
          navButtonsAlwaysVisible={
            cameraData.directions.length + (cameraData.videoUrl ? 1 : 0) > 1
          }
        >
          {[
            // Live video is the first slide (when available); auto-advance is disabled above so it
            // isn't swiped away while playing. The N/S snapshots follow as the remaining slides.
            ...(cameraData.videoUrl
              ? [
                  <Box
                    key="video"
                    sx={{
                      width: "100%",
                      aspectRatio: "16 / 9",
                      "& .video-player": { mt: 0, height: "100%" },
                    }}
                  >
                    <VideoPlayer
                      streamUrl={cameraData.videoUrl}
                      posterUrl={posterUrl}
                    />
                  </Box>,
                ]
              : []),
            ...cameraData.directions.map((dir) => {
              let imgUrl;
              if (dir.remoteUrl) {
                imgUrl = dir.remoteUrl;
              } else if (dir.code && dir.code !== "NONE") {
                imgUrl = `${API_HOST}/snapshot?id=${encodedId}&direction=${dir.code}`;
              } else {
                imgUrl = `${API_HOST}/snapshot?id=${encodedId}`;
              }
              const refPol = imgUrl.includes("cctv.travelmidwest.com")
                ? "origin"
                : "no-referrer";
              return (
                <Card
                  key={dir.code}
                  style={{ border: "none", boxShadow: "none" }}
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                >
                  <Box
                    onClick={() => {
                      const params = new URLSearchParams({
                        id: feature.properties.id,
                      });
                      if (dir.code && dir.code !== "NONE")
                        params.set("direction", dir.code);
                      window.open(
                        `${process.env.PUBLIC_URL}/showCamera?${params.toString()}`,
                        "_blank",
                        `noopener,noreferrer,width=${window.screen.availWidth},height=${window.screen.availHeight},left=0,top=0`,
                      );
                    }}
                    sx={{ cursor: "pointer" }}
                  >
                    <CardMedia
                      component="img"
                      sx={{ height: 190 }}
                      referrerPolicy={refPol}
                      image={imgUrl}
                      alt={`${cameraData.locationDescription} - ${dir.code}`}
                    />
                  </Box>
                  {dir.displayName && dir.displayName !== "None" && (
                    <CardActionArea
                      sx={{
                        textAlign: "center",
                        border: "none",
                        paddingTop: 1,
                        marginBottom: 0,
                      }}
                    >
                      {dir.displayName}
                    </CardActionArea>
                  )}
                </Card>
              );
            }),
          ]}
        </Carousel>
      </CardContent>
    </Card>
  );
};

const createClusterIcon = (cluster) => {
  return L.divIcon({
    html: `<span title='Click to See Icons'></span>`,
    className: "cluster-camera",
    iconSize: L.point(21, 21, true),
  });
};

const module = {
  name: "Cameras",
  endpoint: "/cameraMap.json",
  showOnMap: true,
  getLeafletIcon: getLeafletIcon,
  PopupTemplate: PopupTemplate,
  // getTooltip: getTooltip,
  updateDelay: 180,
  mapLayerIcon: process.env.PUBLIC_URL + "/mapicons/Camera/Camera_A_v9.png",
  clusterLayer: true,
  iconCreateFunction: createClusterIcon,
  maxZoom: 10,
  zIndex: 618,
};

export default module;
